import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import enUS from "antd/locale/en_US";
import zhCN from "antd/locale/zh_CN";
import { messages, type Locale } from "./messages";

type ThemeMode = "light" | "dark";
type ConfigContextValue = {
  locale: Locale; setLocale: (locale: Locale) => void;
  theme: ThemeMode; setTheme: (theme: ThemeMode) => void;
  text: typeof messages.en | typeof messages.zh;
};
const AppConfigContext = createContext<ConfigContextValue | null>(null);

export function AppConfigProvider({ children }: { children: ReactNode }) {
  const [locale,setLocaleState] = useState<Locale>(() => (localStorage.getItem("autoflow.locale") as Locale) || "en");
  const [theme,setThemeState] = useState<ThemeMode>(() => (localStorage.getItem("autoflow.theme") as ThemeMode) || "light");
  const setLocale = (next: Locale) => { localStorage.setItem("autoflow.locale", next); setLocaleState(next); };
  const setTheme = (next: ThemeMode) => { localStorage.setItem("autoflow.theme", next); setThemeState(next); };
  const value = useMemo(() => ({ locale,setLocale,theme,setTheme,text:messages[locale] }), [locale,theme]);
  return <AppConfigContext.Provider value={value}><ConfigProvider locale={locale==="zh"?zhCN:enUS} theme={{
    algorithm: theme==="dark"?antdTheme.darkAlgorithm:antdTheme.defaultAlgorithm,
    token: { colorPrimary:"#2563eb",borderRadius:8,fontFamily:"Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif" }
  }}>{children}</ConfigProvider></AppConfigContext.Provider>;
}

export function useAppConfig() {
  const value = useContext(AppConfigContext);
  if (!value) throw new Error("useAppConfig must be used inside AppConfigProvider");
  return value;
}
